'use client';

import { CaretDown, X } from '@phosphor-icons/react';
import * as React from 'react';

import { cn } from '../lib/cn';

import { Badge } from './badge';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  maxDisplay?: number;
  className?: string;
}

export function MultiSelect({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Select items',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results.',
  disabled,
  maxDisplay = 3,
  className,
}: MultiSelectProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [internal, setInternal] = React.useState<string[]>(defaultValue ?? []);
  const isControlled = value !== undefined;
  const current = isControlled ? (value ?? []) : internal;

  const update = (next: string[]): void => {
    if (!isControlled) setInternal(next);
    onValueChange?.(next);
  };

  const toggle = (val: string): void => {
    update(current.includes(val) ? current.filter((v) => v !== val) : [...current, val]);
  };

  const remove = (val: string): void => {
    update(current.filter((v) => v !== val));
  };

  const selectedOptions = options.filter((opt) => current.includes(opt.value));
  const displayed = selectedOptions.slice(0, maxDisplay);
  const overflow = selectedOptions.length - displayed.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('h-auto min-h-10 w-full justify-between gap-2 py-2 font-normal', className)}
        >
          <div className="flex flex-1 flex-wrap items-center gap-1">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              <>
                {displayed.map((opt) => (
                  <Badge key={opt.value} variant="secondary" className="gap-1 pr-1">
                    {opt.label}
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Remove ${opt.label}`}
                      className="ring-offset-background focus:ring-ring rounded-full outline-none hover:opacity-80 focus:ring-2 focus:ring-offset-1"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          remove(opt.value);
                        }
                      }}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        remove(opt.value);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </Badge>
                ))}
                {overflow > 0 ? <Badge variant="secondary">+{overflow}</Badge> : null}
              </>
            )}
          </div>
          <CaretDown className="text-muted-foreground h-4 w-4 shrink-0 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const checked = current.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggle(option.value)}
                  >
                    <span
                      className={cn(
                        'border-input mr-2 flex h-4 w-4 items-center justify-center rounded-sm border',
                        checked && 'bg-primary border-primary text-primary-foreground',
                      )}
                      aria-hidden="true"
                    >
                      {checked ? <span className="h-2 w-2 rounded-sm bg-current" /> : null}
                    </span>
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
